import React from 'react';
import { useParams } from 'react-router-dom';
import { Card, Spinner } from 'flowbite-react';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useProject } from '../../hooks/useProject';
import EditableTitle from '../../components/EditableTitle';
import RemoteReferenceIcon from '../../components/RemoteReferenceIcon';
import WorkMatchList from '@/components/schedule/WorkMatchList';
import WorkMatchPairForm from '@/components/schedule/WorkMatchPairForm';

const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project, loading, error } = useProject(projectId);
  const { applyCommand } = useDatabase();

  // Function to update the project title
  const updateProjectTitle = async (newTitle: string) => {
    if (!projectId || !project) return;

    try {
      // Create command with the correct type structure
      const command = {
        type: "UpdateProject",
        id: projectId,
        actions: [
          {
            type: "SetName",
            name: newTitle
          }
        ]
      } as unknown as wasm.DatabaseCommand;

      await applyCommand(command);
    } catch (error) {
      console.error('Error updating project title:', error);
      throw error; // Let the EditableTitle component handle the error
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'Project not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <EditableTitle
          title={project.name}
          onTitleChange={updateProjectTitle}
          size="lg"
          className="flex-grow"
        />

        {/* Remote reference icon */}
        <RemoteReferenceIcon
          reference={project.remoteReference}
          showLabel={true}
          className="ml-2"
        />
      </div>


      {/* Work matching */}
      {projectId && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-3">Work Matching</h2>
          <Card>
            <div className="p-4">
              <p className="text-sm text-cm-text-secondary mb-3">Set individual work assignments or general work matches to enable automatic scheduling.</p>
              <WorkMatchList type="object" object={{ ProjectId: { id: projectId } }} />
              <WorkMatchPairForm
                context={{
                  kind: 'Project',
                  id: projectId,
                  projectId: null,
                }}
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailPage;
